import {
  isDate,
  isDateString,
  isDefined,
  isEmail,
  isInt,
  isNumberString,
  isObject,
  isString
} from 'class-validator';
import { camelCase, escape as escapeHtml, unescape as unescapeHtmlEntities } from 'lodash';
import { DateTime } from 'luxon';

import { formatDateTimeWithMilitarySupport, LuxonDateTimeFormats } from './chronon';
import type { Maybe } from './globals';
import { t } from './i18n';
import { formatPhoneNumber, isPhoneValid, isTrulyEmpty, truncateString } from './js-helpers';
import { extractTextFromHtml } from './js-helpers/str';
import { GlobalLimits } from './limits';
import { isCarrierUser, IUser } from './user';
import type { IWarehouse } from './warehouse';

export enum CustomFieldType {
  String = 'str',
  BigString = 'bigstr',
  Date = 'date',
  Bool = 'bool',
  Document = 'doc',
  MultiDocument = 'multidoc',
  Number = 'int',
  Email = 'email',
  Phone = 'phone',
  DropDown = 'dropdown',
  DropDownMultiSelect = 'dropdownmultiselect',
  ComboBox = 'combobox',
  Timestamp = 'timestamp',
  Action = 'action'
}

/**
 * One-directional compatible type transitions for existing fields.
 * Only smaller-to-larger magnitude upgrades are allowed to prevent data loss
 * (e.g. multiple stored values would be silently discarded on a multi→single downgrade).
 */
export const COMPATIBLE_FIELD_TYPE_TRANSITIONS: Partial<
  Record<CustomFieldType, CustomFieldType[]>
> = {
  [CustomFieldType.String]: [CustomFieldType.BigString],
  [CustomFieldType.Document]: [CustomFieldType.MultiDocument],
  [CustomFieldType.DropDown]: [CustomFieldType.DropDownMultiSelect]
};

export function isCompatibleFieldTypeTransition(
  from: CustomFieldType,
  to: CustomFieldType
): boolean {
  if (from === to) {
    return true;
  }
  return COMPATIBLE_FIELD_TYPE_TRANSITIONS[from]?.includes(to) ?? false;
}

export const FIELDS_WITHOUT_PLACEHOLDER = [
  CustomFieldType.Document,
  CustomFieldType.MultiDocument,
  CustomFieldType.DropDown,
  CustomFieldType.DropDownMultiSelect,
  CustomFieldType.Date,
  CustomFieldType.Action,
  CustomFieldType.Bool,
  CustomFieldType.Timestamp
];

export const CUSTOM_FIELD_DROP_DOWN_OPTIONS_LIMIT = 1500;

export const customFieldLabels = {
  [CustomFieldType.String]: 'Text - Single Line',
  [CustomFieldType.BigString]: 'Text - Multiple Lines',
  [CustomFieldType.Date]: 'Date',
  [CustomFieldType.Bool]: 'True/False',
  [CustomFieldType.Document]: 'Single Document',
  [CustomFieldType.MultiDocument]: 'Multi Document',
  [CustomFieldType.Number]: 'Number',
  [CustomFieldType.Email]: 'E-mail',
  [CustomFieldType.Phone]: 'Phone',
  [CustomFieldType.DropDown]: 'Drop-down Single Select List',
  [CustomFieldType.DropDownMultiSelect]: 'Drop-down Multi Select List',
  [CustomFieldType.ComboBox]: 'Combo Box',
  [CustomFieldType.Timestamp]: 'Timestamp',
  [CustomFieldType.Action]: 'Feature Link'
};

export function getTranslatedCustomFieldLabels(): Record<CustomFieldType, string> {
  return Object.values(CustomFieldType).reduce<Record<CustomFieldType, string>>(
    (acc, type) => {
      acc[type] = t(`core.customFields.labels.${camelCase(customFieldLabels[type])}`);
      return acc;
    },
    {} as Record<CustomFieldType, string>
  ) satisfies Record<CustomFieldType, string>;
}

export const customFieldDescriptions = {
  [CustomFieldType.String]: 'Open text field, single line',
  [CustomFieldType.BigString]: 'Open text field, multiple lines',
  [CustomFieldType.Date]: 'Format MM/DD/YYYY',
  [CustomFieldType.Bool]: 'Toggle Button',
  [CustomFieldType.Document]: 'Single file upload field',
  [CustomFieldType.MultiDocument]: 'Multiple file upload field',
  [CustomFieldType.Number]: ' Numeric input field',
  [CustomFieldType.Email]: 'Email address input field',
  [CustomFieldType.Phone]: 'Format +1(XXX) XXX-XXXX',
  [CustomFieldType.DropDown]: 'Select one option from a list',
  [CustomFieldType.DropDownMultiSelect]: 'Select multiple options from a list',
  [CustomFieldType.ComboBox]: 'Single option drop-down list + open text field',
  [CustomFieldType.Timestamp]: 'Date and time fields',
  [CustomFieldType.Action]: 'Link to the form features'
};

export function getTranslatedCustomFieldDescriptions(): Record<CustomFieldType, string> {
  return Object.values(CustomFieldType).reduce<Record<CustomFieldType, string>>(
    (acc, type) => {
      acc[type] = t(`core.customFields.descriptions.${camelCase(customFieldDescriptions[type])}`);
      return acc;
    },
    {} as Record<CustomFieldType, string>
  ) satisfies Record<CustomFieldType, string>;
}

export const customFieldIcons = {
  [CustomFieldType.String]: 'format-letter-case',
  [CustomFieldType.BigString]: 'text-box-outline',
  [CustomFieldType.Date]: 'calendar-blank',
  [CustomFieldType.Bool]: 'toggle-switch-off-outline',
  [CustomFieldType.Document]: 'file-outline',
  [CustomFieldType.MultiDocument]: 'file-multiple-outline',
  [CustomFieldType.Number]: 'numeric',
  [CustomFieldType.Email]: 'at',
  [CustomFieldType.Phone]: 'phone-outline',
  [CustomFieldType.DropDown]: 'arrow-down-drop-circle-outline',
  [CustomFieldType.DropDownMultiSelect]: 'checkbox-multiple-outline',
  [CustomFieldType.ComboBox]: 'form-dropdown',
  [CustomFieldType.Timestamp]: 'clock-time-three-outline',
  [CustomFieldType.Action]: 'link-box-variant-outline'
};

export const MULTI_DOC_MAX_FILES_PER_APPOINTMENT =
  GlobalLimits.MULTI_DOC_MAX_FILES_PER_APPOINTMENT.value;

export function textCustomFieldLenghtIsOk(customField: ICustomField): boolean {
  return (
    (!isInt(customField.minLengthOrValue) ||
      String(customField.value).length >= customField.minLengthOrValue) &&
    (!isInt(customField.maxLengthOrValue) ||
      String(customField.value).length <= customField.maxLengthOrValue)
  );
}

export function numberCustomFieldRangeIsOk(customField: ICustomField): boolean {
  const value = Number(customField.value);
  return (
    (!isInt(customField.minLengthOrValue) || value >= customField.minLengthOrValue) &&
    (!isInt(customField.maxLengthOrValue) || value <= customField.maxLengthOrValue)
  );
}

export function isValidCustomField(customField: ICustomField): boolean {
  if (!(customField instanceof Object) || !(customField.type || customField.value)) {
    return false;
  }

  const value = customField.value;
  const isValidTextField = Boolean(isString(value)) && textCustomFieldLenghtIsOk(customField);

  return {
    [CustomFieldType.String]: isValidTextField,
    [CustomFieldType.BigString]: isValidTextField,
    [CustomFieldType.ComboBox]: isValidTextField,
    [CustomFieldType.Date]: Boolean(isDateString(value) || isDate(value)),
    [CustomFieldType.Bool]: value === true || value === false,
    [CustomFieldType.Document]: Boolean(isString(value)) && String(value).length > 0,
    [CustomFieldType.MultiDocument]:
      Array.isArray(value) &&
      value.length <= MULTI_DOC_MAX_FILES_PER_APPOINTMENT &&
      value.every(item => Boolean(isString(item)) && String(item).length > 0),
    [CustomFieldType.Number]: isInt(Number(value)) && numberCustomFieldRangeIsOk(customField),
    [CustomFieldType.Email]: Boolean(isEmail(value)),
    [CustomFieldType.Phone]: isPhoneValid(String(value)),
    [CustomFieldType.Timestamp]: isDateString(value),
    [CustomFieldType.DropDown]: Boolean(customField.dropDownValues?.includes(String(value))),
    [CustomFieldType.Action]: true,
    [CustomFieldType.DropDownMultiSelect]:
      Boolean((value as Array<string>)?.length === 0) ||
      Boolean(
        Array.isArray(value) &&
        (value as Array<string>)?.every(i => customField.dropDownValues?.includes(i))
      )
  }[customField.type];
}

export interface ICustomField {
  label: string;
  type: CustomFieldType;
  name: string; // used as unique to index fields
  description?: string;
  order?: number;
  value?: string | boolean | number | string[] | null;
  required?: boolean; // Used for api validation
  requiredForWarehouse?: boolean; // Used for frontend validation
  requiredForCarrier?: boolean; // Used for frontend validation
  dropDownValues?: string[]; // this will fill dropdown options
  hiddenFromCarrier?: boolean;
  placeholder?: string;
  minLengthOrValue?: number; // Number of chars if text type OR absolute number if number type
  maxLengthOrValue?: number;
  semanticLabelId?: string | null;
}

export type testCustomField = Partial<ICustomField> & {
  typeLabel: string;
  options?: Record<string, unknown>;
  items?: unknown[];
};

// Formatting options keyed by custom field type go here
export type CustomFieldFormattingOpts = {
  [CustomFieldType.Document]?: {
    generateLink: boolean;
  };
  [CustomFieldType.MultiDocument]?: {
    generateLink: boolean;
  };
  [CustomFieldType.Timestamp]?: {
    timezone: Maybe<string>;
    twelveHoursFormat?: LuxonDateTimeFormats;
    formatAsMilitary: boolean;
    twentyFourHoursFormat?: LuxonDateTimeFormats;
  };
};

/** Only http(s) URLs may be used in generated email/UI anchor hrefs. */
export function isSafeCustomFieldDocumentUrl(url: string): boolean {
  if (!url || !isString(url)) {
    return false;
  }
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
}

function formatSingleDocumentFieldValue(
  value: string,
  generateLink: boolean | undefined
): string | undefined {
  if (!value) {
    return;
  }
  const str = String(value);
  const displayName = getDocumentNameFromUrl(str);

  if (generateLink) {
    if (isSafeCustomFieldDocumentUrl(str)) {
      const label = isString(displayName) && displayName !== '' ? displayName : 'Download';
      return `<a href="${escapeHtml(str)}">${escapeHtml(label)}</a>`;
    }
    const fallback = isString(displayName) && displayName !== '' ? displayName : str;
    return escapeHtml(fallback);
  }

  if (isString(displayName) && displayName !== '') {
    return escapeHtml(displayName);
  }
  return escapeHtml(str);
}

/**
 * Resolves multiselect values that may be stored as a JSON string (API / websocket) or as
 * primitives / `{ label, value }` option objects, into plain display parts.
 */
function normalizeDropDownMultiSelectToDisplayParts(value: unknown): string[] | null {
  let rawItems: unknown[];

  if (Array.isArray(value)) {
    rawItems = value;
  } else if (isString(value)) {
    const trimmed = value.trim();
    if (!trimmed) {
      return [];
    }
    try {
      const parsed: unknown = JSON.parse(trimmed);
      if (Array.isArray(parsed)) {
        rawItems = parsed;
      } else {
        rawItems = [parsed];
      }
    } catch {
      rawItems = [trimmed];
    }
  } else if (!isDefined(value)) {
    return null;
  } else {
    rawItems = [value];
  }

  const parts: string[] = [];
  for (const item of rawItems) {
    if (isDefined(item)) {
      if (isObject(item) && !Array.isArray(item)) {
        const o = item as Record<string, unknown>;
        if (isString(o.label) && o.label.trim() !== '') {
          parts.push(o.label.trim());
        } else if (isDefined(o.value) && (isString(o.value) || typeof o.value === 'number')) {
          const t = String(o.value).trim();
          if (t !== '') {
            parts.push(t);
          }
        }
      } else {
        const t = String(item).trim();
        if (t !== '') {
          parts.push(t);
        }
      }
    }
  }
  return parts;
}

/** Bool values may be stored as booleans, `"true"`/`"false"`, or already-formatted `"Yes"`/`"No"`. */
function customFieldBoolValueIsTruthy(value: unknown): boolean {
  if (isString(value)) {
    const normalized = value.trim().toLowerCase();
    if (normalized === 'true') {
      return true;
    }
    if (normalized === 'false') {
      return false;
    }
  }
  return Boolean(value);
}

// TODO: This seems like it will be deprecated and will need a new version to match new UI designs
//  Update - maybe this will not be deprecated...
// eslint-disable-next-line complexity -- one branch per CustomFieldType
export function getCustomFieldFormattedValue(
  customField: ICustomField,
  opts: CustomFieldFormattingOpts = {}
) {
  if (!customField) {
    return null;
  }

  if (customField.type === CustomFieldType.Bool) {
    return customFieldBoolValueIsTruthy(customField.value) ? 'Yes' : 'No';
  }

  if (customField.type === CustomFieldType.Document) {
    return formatSingleDocumentFieldValue(
      customField.value as string,
      opts?.[CustomFieldType.Document]?.generateLink
    );
  }

  if (customField.type === CustomFieldType.MultiDocument) {
    // A plain URL string here means the value was originally stored as a single-document field
    // (or castField failed to parse a JSON array). Treat it as a single-item array so it still
    // renders as a document link rather than falling through to raw text display.
    const multiDocValues = Array.isArray(customField.value)
      ? (customField.value as string[])
      : isString(customField.value) && customField.value
        ? [customField.value as string]
        : [];

    if (multiDocValues.length === 0) {
      return null;
    }

    return multiDocValues
      .map((item: string) =>
        formatSingleDocumentFieldValue(item, opts?.[CustomFieldType.MultiDocument]?.generateLink)
      )
      .join('<br />');
  }

  if (customField.type === CustomFieldType.DropDownMultiSelect) {
    const parts = normalizeDropDownMultiSelectToDisplayParts(customField.value);
    if (!parts?.length) {
      return null;
    }
    return parts.map(p => escapeHtml(unescapeHtmlEntities(p))).join(', ');
  }

  if (customField.type === CustomFieldType.Date) {
    // Custom fields are stored as ISO String Dates (YYYY-MM-DD)
    const value = String(customField.value);
    const dt = DateTime.fromISO(value);
    return dt.isValid ? dt.toFormat('MM/dd/yyyy') : escapeHtml(unescapeHtmlEntities(value));
  }

  if (customField.type === CustomFieldType.Phone) {
    return escapeHtml(formatPhoneNumber(String(customField.value)));
  }

  if (customField.type === CustomFieldType.Timestamp) {
    if (DateTime.fromISO(String(customField.value)).isValid) {
      return formatDateTimeWithMilitarySupport(
        String(customField.value),
        opts?.[CustomFieldType.Timestamp]?.timezone,
        opts?.[CustomFieldType.Timestamp]?.twelveHoursFormat ||
          LuxonDateTimeFormats.MonthDayYearSlashedTimeAMPM,
        opts?.[CustomFieldType.Timestamp]?.formatAsMilitary || false,
        opts?.[CustomFieldType.Timestamp]?.twentyFourHoursFormat ||
          LuxonDateTimeFormats.MonthDayYearSlashedTime24
      );
    }
    return escapeHtml(unescapeHtmlEntities(String(customField.value)));
  }

  if (customField.type === CustomFieldType.BigString && isString(customField.value)) {
    return escapeHtml(unescapeHtmlEntities(customField.value)).replace(/\n/gu, '<br />');
  }

  if (customField.type === CustomFieldType.Number) {
    return customField.value as number;
  }

  if (customField.value === null) {
    return null;
  }

  if (typeof customField.value === 'undefined') {
    return;
  }

  if (typeof customField.value === 'number') {
    return customField.value;
  }

  return escapeHtml(unescapeHtmlEntities(String(customField.value)));
}

/**
 * Returned by {@link formatGridTileCustomFormFieldDisplay} for empty, unsupported, or unknown values.
 * Calendar load-type grid tiles treat this as “no value” and hide the tile instead of showing it.
 */
export const GRID_TILE_CUSTOM_FORM_FIELD_EMPTY_DISPLAY = '??';

/**
 * `getCustomFieldFormattedValue` may return HTML (e.g. `<br />`, escaped entities). Grid tiles
 * render as plain text — reuse {@link extractTextFromHtml} (script/style stripping + tag removal),
 * then decode entities. `<br />` is replaced with space first so adjacent lines are not glued together.
 */
function gridTileCustomFieldFormattedValueToPlainText(
  formatted: string | number | boolean
): string {
  let s = String(formatted).replace(/<br\s*\/?>/giu, ' ');
  s = extractTextFromHtml(s);
  s = unescapeHtmlEntities(s);
  return s.replace(/\u00a0/gu, ' ').trim();
}

/**
 * Single formatter for compact grid tiles (custom appointment fields + load-type form fields).
 * Matches CustomFieldDataBlock behavior: document types unsupported, values truncated for the grid.
 * Empty or unsupported values resolve to {@link GRID_TILE_CUSTOM_FORM_FIELD_EMPTY_DISPLAY}; Helios hides
 * load-type (`ltcf:`) tiles when the formatted value equals that placeholder.
 */
export function formatGridTileCustomFormFieldDisplay(
  field: Pick<ICustomField, 'type' | 'value' | 'label'> & { name?: string },
  opts: { timezone: string; formatAsMilitary: boolean }
): string {
  if (field.type === CustomFieldType.Document || field.type === CustomFieldType.MultiDocument) {
    return GRID_TILE_CUSTOM_FORM_FIELD_EMPTY_DISPLAY;
  }
  if (isTrulyEmpty(field.value)) {
    return GRID_TILE_CUSTOM_FORM_FIELD_EMPTY_DISPLAY;
  }

  const asCustomField = { ...field, name: field.name ?? field.label } as ICustomField;
  const formatted = getCustomFieldFormattedValue(asCustomField, {
    [CustomFieldType.Timestamp]: {
      timezone: opts.timezone,
      formatAsMilitary: opts.formatAsMilitary
    }
  });

  if (!isDefined(formatted)) {
    return GRID_TILE_CUSTOM_FORM_FIELD_EMPTY_DISPLAY;
  }

  const plain = gridTileCustomFieldFormattedValueToPlainText(formatted);
  if (plain === '') {
    return GRID_TILE_CUSTOM_FORM_FIELD_EMPTY_DISPLAY;
  }

  return truncateString(plain, 20);
}

export function getDocumentNameFromUrl(url: string, limit = 50): Maybe<string> {
  if (!url) {
    return null;
  }
  const urlParts = url.split('/');
  const lastSegment = urlParts[urlParts.length - 1];
  let fileName: string;
  if (lastSegment) {
    try {
      fileName = decodeURIComponent(lastSegment);
    } catch {
      fileName = lastSegment;
    }
  } else {
    fileName = 'Download';
  }
  if (fileName !== 'Download') {
    if (fileName.length > limit) {
      fileName = `${fileName.substring(0, limit - 3)}...`;
    }
  }
  return fileName;
}

export function getDefaultCustomFieldName(field: ICustomField) {
  if (field.type && field.label) {
    return `${field.type}${field.label}`;
  }

  return 'NO_FIELD_TYPE_AND_LABEL';
}

/*
 * This function updates the appointment custom fields according to the current warehouse rules for the custom field
 */
export function updateCustomFieldsFromWarehouse(
  existingCustomFields: ICustomField[],
  warehouse: IWarehouse,
  isCarrierView = false
) {
  let fieldsFromAppointment: ICustomField[] | null = existingCustomFields;
  if (isTrulyEmpty(existingCustomFields) || !Array.isArray(existingCustomFields)) {
    fieldsFromAppointment = null;
  }

  const customFields =
    fieldsFromAppointment ||
    warehouse?.customApptFieldsTemplate
      ?.filter(f => (isCarrierView ? !f.hiddenFromCarrier : true))
      .map(field => ({
        ...field,
        name: field.name ?? getDefaultCustomFieldName(field)
      })) ||
    [];

  // Get required information from warehouse template
  if (Array.isArray(customFields)) {
    return customFields.map(customField => {
      const fieldTemplate = warehouse?.customApptFieldsTemplate?.find(
        fieldTemplate =>
          fieldTemplate.label === customField.label && fieldTemplate.type === customField.type
      );
      if (isCarrierView) {
        customField.hiddenFromCarrier = fieldTemplate?.hiddenFromCarrier ?? false;
      }

      customField.required = getRequiredValue(fieldTemplate ?? customField, isCarrierView);

      return customField;
    });
  }

  return [];
}

export function getRequiredValue(field: ICustomField, isCarrierView = false) {
  if (!field) {
    return false;
  }
  if (isObject(field)) {
    if (isCarrierView) {
      if (Object.prototype.hasOwnProperty.call(field, 'requiredForCarrier')) {
        return Boolean(field.requiredForCarrier);
      }
    } else {
      if (Object.prototype.hasOwnProperty.call(field, 'requiredForWarehouse')) {
        return Boolean(field.requiredForWarehouse);
      }
    }
    return Boolean(field.required);
  }
  return false;
}

export function removeHiddenFromCarrierFields(
  customFields: Maybe<ICustomField[]>
): Maybe<ICustomField[]> {
  return customFields ? customFields.filter(field => !field.hiddenFromCarrier) : customFields;
}

export function mergeCustomFieldsWithUserRestrictions(
  customFields: ICustomField[],
  customFieldsTemplate: ICustomField[],
  user: IUser
) {
  // Sucks, but order matters here to keep current functionality,
  // so I put this as an else if so you know these go together
  if (!customFields) {
    return customFieldsTemplate;
  } else if (!customFieldsTemplate) {
    return [];
  }

  return customFieldsTemplate.map(item => {
    const match = customFields.find(bItem => {
      const rejectUpdateForCarrier = item.hiddenFromCarrier && isCarrierUser(user);
      const foundItem = bItem.label === item.label && bItem.type === item.type;
      return foundItem && !rejectUpdateForCarrier;
    });
    if (match) {
      return {
        ...item,
        value: match.value
      };
    }
    return item;
  });
}

/**
 * "required" was deprecated, but it still exists for any customField set prior to it being deprecated.
 * Because it may still be on appointments WITHOUT requiredForX, we still need to check it, but only if
 * the appointment and/or warehouse do not have requiredForX values.
 */
export function hasRequiredCustomFieldsForUserType(customFields: ICustomField[], user: IUser) {
  if (customFields?.length) {
    if (isCarrierUser(user)) {
      return customFields.find((t: ICustomField) => t.requiredForCarrier ?? t.required)?.label;
    }
    return customFields.find((t: ICustomField) => t.requiredForWarehouse)?.label;
  }
}

export function castField(
  type: CustomFieldType,
  value: string
): Maybe<string | boolean | number | string[]> {
  if (!isDefined(value)) {
    return null;
  }

  let convertedValue: Maybe<string | boolean | number | string[]>;
  try {
    switch (type) {
      case CustomFieldType.Bool:
        value = value?.toLowerCase();
        convertedValue = value !== 'true' && value !== 'false' ? null : Boolean(value === 'true');
        break;

      case CustomFieldType.Number:
        convertedValue = isNumberString(value) ? Number(value) : null;
        break;

      case CustomFieldType.MultiDocument:
        convertedValue = value ? JSON.parse(value) : null;
        break;

      case CustomFieldType.DropDownMultiSelect:
        convertedValue = value ? JSON.parse(value) : null;
        break;

      default:
        convertedValue = value;
    }
  } catch {
    convertedValue = value;
  }

  return convertedValue;
}

/*
 * This function checks if the custom fields should be enabled for the warehouse
 * When the warehouse never set the customApptFieldsTemplate, the custom fields should not be enabled
 * Then the custom forms, appointment per loadtype should be used instead
 * @param warehouse: IWarehouse
 * @returns boolean
 */
export function shouldEnableCustomFields(warehouse: IWarehouse): boolean {
  return Boolean(
    warehouse?.customApptFieldsTemplate &&
    Array.isArray(warehouse.customApptFieldsTemplate) &&
    warehouse.customApptFieldsTemplate.length > 0
  );
}

/**
 * Resolves the custom field type from a string
 * @param typeString - The string to resolve the custom field type from
 * @returns The resolved CustomFieldType enum value or undefined if the typeString is not a valid custom field type
 */
export function resolveCustomFieldType(typeString: string): CustomFieldType | undefined {
  return Object.values(CustomFieldType).find(type => type === typeString);
}
